import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getFormUrl } from '@/utils/form';

export default function Contact() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'visita' | 'contacto'>('visita');

  // Agendar Visita form state
  const [visitaStatus, setVisitaStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [visitaError, setVisitaError] = useState('');
  const [visitaData, setVisitaData] = useState({
    name: '',
    email: '',
    phone: '',
    preferredDate: '',
    message: '',
  });
  const [visitaPhoneAlt, setVisitaPhoneAlt] = useState('');

  // Contacto form state
  const [contactoStatus, setContactoStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [contactoError, setContactoError] = useState('');
  const [contactoData, setContactoData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [contactoPhoneAlt, setContactoPhoneAlt] = useState('');

  const handleVisitaChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setVisitaData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleContactoChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setContactoData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleVisitaSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (visitaPhoneAlt.trim() !== '') {
      setVisitaStatus('success');
      setVisitaData({ name: '', email: '', phone: '', preferredDate: '', message: '' });
      setVisitaPhoneAlt('');
      return;
    }

    setVisitaStatus('loading');
    setVisitaError('');

    const formUrl = getFormUrl('agendarVisita');
    if (!formUrl) {
      setVisitaStatus('error');
      setVisitaError(t('contact.visita.error'));
      return;
    }

    const now = new Date();
    const dateTimeStr = now.toLocaleString('es-CR', { timeZone: 'America/Costa_Rica' });

    const rawData = new FormData(e.currentTarget);
    const body = new URLSearchParams();
    body.append('name', (rawData.get('name') as string) || visitaData.name);
    body.append('email', (rawData.get('email') as string) || visitaData.email);
    body.append('phone', (rawData.get('phone') as string) || visitaData.phone);
    const dateVal = (rawData.get('preferredDate') as string) || visitaData.preferredDate;
    body.append('subject', `Fecha preferida: ${dateVal || 'No especificada'}`);
    body.append('message', (rawData.get('message') as string) || visitaData.message);
    body.append('fecha_envio', dateTimeStr);
    body.append('_subject', 'San Lorenzo School - Solicitud para agendar visita');
    body.append('_template', 'table');

    try {
      const response = await fetch(formUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      const responseText = await response.text();
      let parsed: { success?: string | boolean; message?: string } = {};
      try { parsed = JSON.parse(responseText); } catch { /* non-JSON response */ }

      const serverMsg = parsed?.message || '';
      const submitSucceeded = response.ok && (parsed?.success === 'true' || parsed?.success === true || !responseText);

      if (submitSucceeded) {
        setVisitaStatus('success');
        setVisitaData({ name: '', email: '', phone: '', preferredDate: '', message: '' });
        setVisitaPhoneAlt('');
      } else {
        setVisitaStatus('error');
        setVisitaError(serverMsg || t('contact.visita.error'));
      }
    } catch {
      setVisitaStatus('error');
      setVisitaError(t('contact.visita.error'));
    }
  };

  const handleContactoSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (contactoPhoneAlt.trim() !== '') {
      setContactoStatus('success');
      setContactoData({ name: '', email: '', phone: '', subject: '', message: '' });
      setContactoPhoneAlt('');
      return;
    }

    setContactoStatus('loading');
    setContactoError('');

    const formUrl = getFormUrl('contacto');
    if (!formUrl) {
      setContactoStatus('error');
      setContactoError(t('contact.form.error'));
      return;
    }

    const now = new Date();
    const dateTimeStr = now.toLocaleString('es-CR', { timeZone: 'America/Costa_Rica' });

    const rawData = new FormData(e.currentTarget);
    const body = new URLSearchParams();
    body.append('name', (rawData.get('name') as string) || contactoData.name);
    body.append('email', (rawData.get('email') as string) || contactoData.email);
    body.append('phone', (rawData.get('phone') as string) || contactoData.phone);
    body.append('subject', (rawData.get('subject') as string) || contactoData.subject);
    body.append('message', (rawData.get('message') as string) || contactoData.message);
    body.append('fecha_envio', dateTimeStr);
    body.append('_subject', 'San Lorenzo School - Formulario de contacto');
    body.append('_template', 'table');

    try {
      const response = await fetch(formUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      const responseText = await response.text();
      let parsed: { success?: string | boolean; message?: string } = {};
      try { parsed = JSON.parse(responseText); } catch { /* non-JSON response */ }

      const serverMsg = parsed?.message || '';
      const submitSucceeded = response.ok && (parsed?.success === 'true' || parsed?.success === true || !responseText);

      if (submitSucceeded) {
        setContactoStatus('success');
        setContactoData({ name: '', email: '', phone: '', subject: '', message: '' });
        setContactoPhoneAlt('');
      } else {
        setContactoStatus('error');
        setContactoError(serverMsg || t('contact.form.error'));
      }
    } catch {
      setContactoStatus('error');
      setContactoError(t('contact.form.error'));
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="relative h-[400px] md:h-[560px] flex items-center justify-center">
        <div className="absolute inset-0">
          <img
            src="https://storage.readdy-site.link/project_files/2dbf3bce-c893-400a-8ef5-86be21e9463b/8e9cff3a-91d9-4597-b802-0a072acdeb58_compressed_ContactoF_comprimida.webp"
            alt={t('contact.title')}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-purple-900/60 via-purple-900/50 to-purple-900/70" />
        </div>
        <div className="relative z-10 text-center px-4">
          <span className="text-yellow-400 text-xs font-semibold tracking-[0.2em] uppercase">{t('contact.heroBadge')}</span>
          <h1 className="font-serif text-4xl md:text-6xl font-bold text-white mt-4 leading-tight">{t('contact.title')}</h1>
          <p className="text-white/70 mt-4 max-w-2xl mx-auto text-base leading-relaxed">{t('contact.subtitle')}</p>
        </div>
      </section>

      {/* Contact Info & Form */}
      <section className="py-20 md:py-28 bg-white">
        <div className="w-full px-4 md:px-8 lg:px-16 max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 md:gap-16">
            {/* Info */}
            <div>
              <span className="text-yellow-500 text-xs font-semibold tracking-[0.2em] uppercase">{t('contact.infoBadge')}</span>
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-purple-900 mt-4 mb-10 leading-tight">{t('contact.title')}</h2>

              <div className="space-y-6">
                {[
                  { icon: 'ri-map-pin-line', title: t('contact.address'), value: t('contact.addressValue') },
                  { icon: 'ri-phone-line', title: t('contact.phone'), value: t('contact.phoneValue'), href: 'tel:+50683495115' },
                  { icon: 'ri-mail-line', title: t('contact.email'), value: t('contact.emailValue'), href: 'mailto:admision@sanlorenzocr.com' },
                  { icon: 'ri-time-line', title: t('contact.hours'), value: t('contact.hoursValue') },
                ].map((item) => (
                  <div key={item.title} className="flex items-start gap-4 p-3 rounded-xl hover:bg-purple-50/60 transition-colors duration-300">
                    <div className="w-12 h-12 rounded-xl bg-teal-500 flex items-center justify-center flex-shrink-0">
                      <i className={`${item.icon} text-yellow-400 text-xl`} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-purple-900 text-sm mb-1">{item.title}</h3>
                      {item.href ? (
                        <a href={item.href} className="text-black/50 text-sm hover:text-purple-800 transition-colors">
                          {item.value}
                        </a>
                      ) : (
                        <p className="text-black/50 text-sm">{item.value}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-10">
                <h3 className="font-semibold text-purple-900 mb-4 text-sm">{t('contact.followUs')}</h3>
                <div className="flex items-center gap-2">
                  {[
                    { icon: 'ri-facebook-fill', href: 'https://www.facebook.com/SanLorenzoSchoolCR' },
                    { icon: 'ri-instagram-line', href: 'https://www.instagram.com/sanlorenzoschoolcr' },
                    { icon: 'ri-youtube-line', href: 'https://www.youtube.com/@SanLorenzoSchoolCR' },
                    { icon: 'ri-linkedin-fill', href: 'https://www.linkedin.com/school/san-lorenzo-school-cr' },
                  ].map((social) => (
                    <a
                      key={social.icon}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 rounded-xl bg-purple-800 flex items-center justify-center hover:bg-yellow-500 social-icon-hover"
                    >
                      <i className={`${social.icon} text-white text-sm`} />
                    </a>
                  ))}
                </div>
              </div>
            </div>

            {/* Form with Tabs */}
            <div className="bg-purple-50 rounded-2xl p-6 md:p-8 border border-purple-100">
              {/* Tabs */}
              <div className="flex flex-col sm:flex-row bg-purple-200/60 rounded-2xl sm:rounded-full p-1.5 sm:p-1 mb-8 gap-1.5 sm:gap-0" role="tablist" aria-label={t('contact.title')}>
                <button
                  onClick={() => setActiveTab('visita')}
                  role="tab"
                  aria-selected={activeTab === 'visita'}
                  aria-controls="panel-visita"
                  id="tab-visita"
                  className={`flex-1 px-4 py-2.5 rounded-xl sm:rounded-full text-sm font-semibold transition-all duration-300 whitespace-nowrap ${
                    activeTab === 'visita'
                      ? 'bg-purple-800 text-white shadow-sm'
                      : 'text-black/60 hover:text-purple-800'
                  }`}
                >
                  <i className="ri-calendar-check-line mr-1.5" />
                  {t('contact.visitaTab')}
                </button>
                <button
                  onClick={() => setActiveTab('contacto')}
                  role="tab"
                  aria-selected={activeTab === 'contacto'}
                  aria-controls="panel-contacto"
                  id="tab-contacto"
                  className={`flex-1 px-4 py-2.5 rounded-xl sm:rounded-full text-sm font-semibold transition-all duration-300 whitespace-nowrap ${
                    activeTab === 'contacto'
                      ? 'bg-purple-800 text-white shadow-sm'
                      : 'text-black/60 hover:text-purple-800'
                  }`}
                >
                  <i className="ri-mail-send-line mr-1.5" />
                  {t('contact.contactoTab')}
                </button>
              </div>

              {/* Agendar Visita Form */}
              {activeTab === 'visita' && (
                <div id="panel-visita" role="tabpanel" aria-labelledby="tab-visita">
                <form id="agendar-visita" data-readdy-form onSubmit={handleVisitaSubmit} className="space-y-4">
                  <h3 className="font-serif text-lg font-semibold text-purple-900 mb-2">{t('contact.visita.title')}</h3>
                  <div>
                    <label className="block text-sm font-medium text-black/70 mb-1.5">{t('contact.form.name')}</label>
                    <input
                      type="text"
                      name="name"
                      value={visitaData.name}
                      onChange={handleVisitaChange}
                      required
                      className="w-full px-4 py-3 rounded-xl border border-purple-200 focus:border-purple-800 focus:ring-1 focus:ring-purple-800 outline-none text-sm bg-white transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-black/70 mb-1.5">{t('contact.form.email')}</label>
                    <input
                      type="email"
                      name="email"
                      value={visitaData.email}
                      onChange={handleVisitaChange}
                      required
                      className="w-full px-4 py-3 rounded-xl border border-purple-200 focus:border-purple-800 focus:ring-1 focus:ring-purple-800 outline-none text-sm bg-white transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-black/70 mb-1.5">{t('contact.form.phone')}</label>
                    <input
                      type="tel"
                      name="phone"
                      value={visitaData.phone}
                      onChange={handleVisitaChange}
                      className="w-full px-4 py-3 rounded-xl border border-purple-200 focus:border-purple-800 focus:ring-1 focus:ring-purple-800 outline-none text-sm bg-white transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-black/70 mb-1.5">{t('contact.visita.preferredDate')}</label>
                    <input
                      type="date"
                      name="preferredDate"
                      value={visitaData.preferredDate}
                      onChange={handleVisitaChange}
                      required
                      className="w-full px-4 py-3 rounded-xl border border-purple-200 focus:border-purple-800 focus:ring-1 focus:ring-purple-800 outline-none text-sm bg-white transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-black/70 mb-1.5">{t('contact.form.message')}</label>
                    <textarea
                      name="message"
                      value={visitaData.message}
                      onChange={handleVisitaChange}
                      rows={4}
                      maxLength={500}
                      className="w-full px-4 py-3 rounded-xl border border-purple-200 focus:border-purple-800 focus:ring-1 focus:ring-purple-800 outline-none text-sm bg-white resize-none transition-colors"
                    />
                    <p className="text-xs text-black/30 mt-1 text-right">{visitaData.message.length}/500</p>
                  </div>
                  <input
                    type="text"
                    name="phone_alt"
                    value={visitaPhoneAlt}
                    onChange={(e) => setVisitaPhoneAlt(e.target.value)}
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    className="visually-hidden-field"
                  />
                  <button
                    type="submit"
                    disabled={visitaStatus === 'loading'}
                    className="w-full px-6 py-3.5 bg-yellow-500 text-white font-semibold rounded-xl hover:bg-yellow-600 hover:shadow-lg hover:shadow-yellow-500/20 transition-all duration-300 text-sm disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {visitaStatus === 'loading' ? t('contact.visita.sending') : t('contact.visita.submit')}
                  </button>
                  {visitaStatus === 'success' && (
                    <p className="text-teal-600 text-sm text-center bg-teal-50 py-3 rounded-xl">{t('contact.visita.success')}</p>
                  )}
                  {visitaStatus === 'error' && (
                    <p className="text-yellow-600 text-sm text-center bg-yellow-500/10 py-3 rounded-xl">{visitaError || t('contact.visita.error')}</p>
                  )}
                </form>
                </div>
              )}

              {/* Contacto Form */}
              {activeTab === 'contacto' && (
                <div id="panel-contacto" role="tabpanel" aria-labelledby="tab-contacto">
                <form id="contacto" data-readdy-form onSubmit={handleContactoSubmit} className="space-y-4">
                  <h3 className="font-serif text-lg font-semibold text-purple-900 mb-2">{t('contact.form.title')}</h3>
                  <div>
                    <label className="block text-sm font-medium text-black/70 mb-1.5">{t('contact.form.name')}</label>
                    <input
                      type="text"
                      name="name"
                      value={contactoData.name}
                      onChange={handleContactoChange}
                      required
                      className="w-full px-4 py-3 rounded-xl border border-purple-200 focus:border-purple-800 focus:ring-1 focus:ring-purple-800 outline-none text-sm bg-white transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-black/70 mb-1.5">{t('contact.form.email')}</label>
                    <input
                      type="email"
                      name="email"
                      value={contactoData.email}
                      onChange={handleContactoChange}
                      required
                      className="w-full px-4 py-3 rounded-xl border border-purple-200 focus:border-purple-800 focus:ring-1 focus:ring-purple-800 outline-none text-sm bg-white transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-black/70 mb-1.5">{t('contact.form.phone')}</label>
                    <input
                      type="tel"
                      name="phone"
                      value={contactoData.phone}
                      onChange={handleContactoChange}
                      className="w-full px-4 py-3 rounded-xl border border-purple-200 focus:border-purple-800 focus:ring-1 focus:ring-purple-800 outline-none text-sm bg-white transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-black/70 mb-1.5">{t('contact.form.subject')}</label>
                    <input
                      type="text"
                      name="subject"
                      value={contactoData.subject}
                      onChange={handleContactoChange}
                      required
                      className="w-full px-4 py-3 rounded-xl border border-purple-200 focus:border-purple-800 focus:ring-1 focus:ring-purple-800 outline-none text-sm bg-white transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-black/70 mb-1.5">{t('contact.form.message')}</label>
                    <textarea
                      name="message"
                      value={contactoData.message}
                      onChange={handleContactoChange}
                      required
                      rows={4}
                      maxLength={500}
                      className="w-full px-4 py-3 rounded-xl border border-purple-200 focus:border-purple-800 focus:ring-1 focus:ring-purple-800 outline-none text-sm bg-white resize-none transition-colors"
                    />
                    <p className="text-xs text-black/30 mt-1 text-right">{contactoData.message.length}/500</p>
                  </div>
                  <input
                    type="text"
                    name="phone_alt"
                    value={contactoPhoneAlt}
                    onChange={(e) => setContactoPhoneAlt(e.target.value)}
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    className="visually-hidden-field"
                  />
                  <button
                    type="submit"
                    disabled={contactoStatus === 'loading'}
                    className="w-full px-6 py-3.5 bg-purple-800 text-white font-semibold rounded-xl hover:bg-purple-900 hover:shadow-lg hover:shadow-purple-800/20 transition-all duration-300 text-sm disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {contactoStatus === 'loading' ? t('contact.form.sending') : t('contact.form.submit')}
                  </button>
                  {contactoStatus === 'success' && (
                    <p className="text-teal-600 text-sm text-center bg-teal-50 py-3 rounded-xl">{t('contact.form.success')}</p>
                  )}
                  {contactoStatus === 'error' && (
                    <p className="text-yellow-600 text-sm text-center bg-yellow-500/10 py-3 rounded-xl">{contactoError || t('contact.form.error')}</p>
                  )}
                </form>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Map */}
      <section className="h-[400px] md:h-[500px]">
        <iframe
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3571.8337348332657!2d-84.0306724!3d9.9305805!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8fa0e3f13dbbbad7%3A0xd5ff47cd01e03068!2sSan%20Lorenzo%20School!5e1!3m2!1ses!2scr!4v1782229621897!5m2!1ses!2scr"
          width="100%"
          height="100%"
          style={{ border: 0 }}
          allowFullScreen
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          title={t('contact.addressValue')}
        />
      </section>
    </div>
  );
}